import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Accept } from 'react-dropzone';

import PDFToolWorkspace from '../components/PDFToolWorkspace';
import ToolActionArea, { ToolStatus, OutputFile } from '../components/ToolActionArea';
import PdfViewerModal, { ViewerFile } from '../components/PdfViewerModal';
import { encryptPdf, downloadFile } from '../utils/pdfUtils';
import StyledFAQ from '../components/StyledFAQ';

interface FileWithId {
    id: string;
    file: File;
}

const PdfEncryptor: React.FC = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<FileWithId[]>([]);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [outputFiles, setOutputFiles] = useState<OutputFile[]>([]);
    const [error, setError] = useState<string>('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [touched, setTouched] = useState({ password: false, confirmPassword: false });

    // Viewer state
    const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);
    const handleOpenFileViewer = (file: ViewerFile) => setViewerFile(file);
    const handleCloseFileViewer = () => setViewerFile(null);

    const resetProcessingState = useCallback(() => {
        setStatus('idle');
        setOutputFiles([]);
        setError('');
    }, []);

    const handleDrop = useCallback((acceptedFiles: File[]) => {
        const newFile = acceptedFiles[acceptedFiles.length - 1]; // Only take the last one for single file tools
        if (newFile) {
            setFiles([{ id: uuidv4(), file: newFile }]);
        }
        resetProcessingState();
    }, [resetProcessingState]);

    const handleDelete = useCallback((fileId: string) => {
        setFiles(currentFiles => currentFiles.filter(f => f.id !== fileId));
    }, []);

    const handleReset = useCallback(() => {
        setFiles([]);
        resetProcessingState();
        setPassword('');
        setConfirmPassword('');
        setTouched({ password: false, confirmPassword: false });
    }, [resetProcessingState]);

    const handleEncrypt = async () => {
        if (!files[0]) return;

        setStatus('loading');
        setError('');
        try {
            const encryptedBytes = await encryptPdf(files[0].file, password);
            const baseName = files[0].file.name.replace(/\.pdf$/i, '');
            const output: OutputFile = {
                name: `${baseName}-encrypted.pdf`,
                data: encryptedBytes,
                size: encryptedBytes.length,
            };
            setOutputFiles([output]);
            setStatus('success');
        } catch (e: any) {
            console.error(e);
            setError(t('pdf_encrypt_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    };

    const handleDownloadSingle = (fileName: string) => {
        const fileToDownload = outputFiles.find(f => f.name === fileName);
        if (fileToDownload) {
            downloadFile(fileToDownload.data, fileToDownload.name);
        }
    };

    const handlePreviewOutput = (fileName: string) => {
        const fileToPreview = outputFiles.find(f => f.name === fileName);
        if (fileToPreview) {
            // Previewing encrypted files directly might not work as expected
            // in some viewers without password prompt, so we let the download be the primary action.
            // For this implementation, we just download it on preview click.
            handleDownloadSingle(fileName);
        }
    };

    const passwordError = useMemo(() => {
        if (touched.password && !password) return t('pdf_encrypt_error_password_required', { ns: 'tools' });
        return '';
    }, [password, touched.password, t]);

    const confirmPasswordError = useMemo(() => {
        if (touched.confirmPassword && !confirmPassword) return t('pdf_encrypt_error_confirm_required', { ns: 'tools' });
        if (confirmPassword && password !== confirmPassword) return t('pdf_encrypt_error_mismatch', { ns: 'tools' });
        return '';
    }, [password, confirmPassword, touched.confirmPassword, t]);

    const isActionDisabled = files.length === 0 || !password || !!passwordError || !!confirmPasswordError || status === 'loading';

    const faqs = [
        { q: 'pdf_encrypt_faq1_q', a: 'pdf_encrypt_faq1_a' },
        { q: 'pdf_encrypt_faq2_q', a: 'pdf_encrypt_faq2_a' },
        { q: 'pdf_encrypt_faq3_q', a: 'pdf_encrypt_faq3_a' },
    ];

    const acceptedFiles: Accept = { 'application/pdf': ['.pdf'] };

    return (
        <>
            <PdfViewerModal
                open={viewerFile !== null}
                onClose={handleCloseFileViewer}
                file={viewerFile}
            />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={4} alignItems="center">
                    <PDFToolWorkspace
                        titleKey="pdf_encrypt"
                        descKey="pdf_encrypt_desc_long"
                        acceptedFiles={acceptedFiles}
                        allowMultiple={false}
                        allowReordering={false}
                        disabled={status === 'loading'}
                        files={files}
                        onDrop={handleDrop}
                        onDelete={handleDelete}
                        onReorder={() => {}} // Not used
                        onFileClick={(file) => handleOpenFileViewer(file)}
                    >
                        {files.length > 0 && status === 'idle' && (
                            <Stack spacing={2} sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    label={t('pdf_encrypt_password_label', { ns: 'tools' })}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onBlur={() => setTouched(prev => ({...prev, password: true}))}
                                    fullWidth
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    type={showPassword ? 'text' : 'password'}
                                    label={t('pdf_encrypt_confirm_password_label', { ns: 'tools' })}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onBlur={() => setTouched(prev => ({...prev, confirmPassword: true}))}
                                    fullWidth
                                    error={!!confirmPasswordError}
                                    helperText={confirmPasswordError}
                                />
                            </Stack>
                        )}
                    </PDFToolWorkspace>

                    <ToolActionArea
                        status={status}
                        onAction={handleEncrypt}
                        onReset={handleReset}
                        onDownloadAll={() => handleDownloadSingle(outputFiles[0]?.name)} // Only one file
                        onPreviewOutput={handlePreviewOutput}
                        actionButtonText={t('pdf_encrypt_action_button', { ns: 'tools' })}
                        successTitle={t('pdf_encrypt_success_title', { ns: 'tools' })}
                        successMessage={t('pdf_encrypt_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        outputFiles={outputFiles}
                        disabled={isActionDisabled}
                    />
                </Stack>

                <Box sx={{ my: 15 }}>
                    <StyledFAQ
                        title={t('faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Container>
        </>
    );
};

export default PdfEncryptor;